import * as THREE from 'three';
import { DEFAULT_BUILDING_WIDTH } from '../geometry/buildingGeometry';

// Cache the grid texture to avoid recreating it
let cachedGridTexture = null;

export const setupLights = (scene) => {
  // Check if lights are already added to avoid re-adding them
  if (!scene.getObjectByName('directionalLight')) {
    const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
    directionalLight.position.set(50, 50, 150);
    directionalLight.name = 'directionalLight';
    scene.add(directionalLight);
  }

  if (!scene.getObjectByName('ambientLight')) {
    const ambientLight = new THREE.AmbientLight(0x404040, 10); // Adjust intensity as needed
    ambientLight.name = 'ambientLight';
    scene.add(ambientLight);
  }
};

export const createPerspectiveCamera = (aspect, fov = 70) => {
  // Building dimensions based on geometry code
  const width = DEFAULT_BUILDING_WIDTH;
  const depth = 21; // Default depth
  
  // Calculate building center
  const centerX = width / 2;
  const centerY = 15; // Approximate vertical center of typical building
  const centerZ = depth / 2;
  
  const camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 20000);
  
  // Position camera relative to building center
  // Adjusted to move the camera more to the right (-X direction) to center the building on screen
  camera.position.set(centerX - 20, centerY + 40, centerZ + 120);
  
  // Look at the building's center
  camera.lookAt(centerX, centerY, centerZ);
  
  return camera;
};

export const createGround = (scene) => {
  // Check if ground is already added to avoid re-adding it
  if (!scene.getObjectByName('ground')) {
    // Use a much larger plane geometry for "infinite" effect
    const geometry = new THREE.PlaneGeometry(10000, 10000);
    
    // Create grid texture for better infinite ground visualization only if not cached
    if (!cachedGridTexture) {
      const gridSize = 32; // Larger canvas for crisper lines
      cachedGridTexture = new THREE.TextureLoader().load(createGridTexture(gridSize));
      cachedGridTexture.wrapS = THREE.RepeatWrapping;
      cachedGridTexture.wrapT = THREE.RepeatWrapping;
      cachedGridTexture.repeat.set(500, 500); // More repetitions for a finer grid
    }
    
    const material = new THREE.MeshStandardMaterial({
      color: 0xf5f5f5,
      map: cachedGridTexture,
      roughness: 0.8,
      receiveShadow: true
    });
    
    const ground = new THREE.Mesh(geometry, material);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.5;
    ground.name = 'ground';
    scene.add(ground);
  }
};

// Helper function to create a grid texture using a canvas
function createGridTexture(size) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  
  // Fill with base color
  context.fillStyle = '#f5f5f5';
  context.fillRect(0, 0, size, size);
  
  // Draw grid lines
  context.strokeStyle = '#d0d0d0'; // Slightly darker grid for better visibility
  context.lineWidth = 1;
  
  // Draw vertical line
  context.beginPath();
  context.moveTo(size-1, 0);
  context.lineTo(size-1, size);
  context.stroke();
  
  // Draw horizontal line
  context.beginPath();
  context.moveTo(0, size-1);
  context.lineTo(size, size-1);
  context.stroke();
  
  return canvas.toDataURL();
}

export const drawAxes = (scene) => {
  if (!scene.getObjectByName('xAxis')) {
    const axesLength = 100;
    const xAxisMaterial = new THREE.LineBasicMaterial({ color: 0xff0000 });
    const yAxisMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
    const zAxisMaterial = new THREE.LineBasicMaterial({ color: 0x0000ff });

    const xAxisGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(axesLength, 0, 0),
    ]);
    const yAxisGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, axesLength, 0),
    ]);
    const zAxisGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, axesLength),
    ]);

    const xAxis = new THREE.Line(xAxisGeometry, xAxisMaterial);
    xAxis.name = 'xAxis';
    const yAxis = new THREE.Line(yAxisGeometry, yAxisMaterial);
    yAxis.name = 'yAxis';
    const zAxis = new THREE.Line(zAxisGeometry, zAxisMaterial);
    zAxis.name = 'zAxis';

    scene.add(xAxis);
    scene.add(yAxis);
    scene.add(zAxis);
  }
};